import React, { forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { PopupRef, PopupProps } from '@/shared/types/popup';
import { usePopup } from '../../hooks/usePopup';
import { cn } from '@/shared/utils';
import { InnerButton } from '@/modules/InnerButton';
import { PopupArrow } from '../molecules/PopupArrow';
import { PopupStructuredLayout } from './PopupStructuredLayout';
import { convertLegacyTooltipToTooltipConfig } from '@/modules/InnerButton/utils/configConverters';
import { ButtonIconConfig, ButtonTooltipConfig } from '@/shared/types/buttonConfig';
import { isButtonSlotSpreadLayout } from '@/shared/types/button';
import { AnyIconDefinition } from '@/shared/types/types';

/**
 * Popup - Componente de popup/dropdown posicionable y configurable
 *
 * Componente de popup que se posiciona relativamente a un trigger, con soporte para múltiples
 * posiciones, cierre automático, tooltips, iconos y slots personalizados.
 *
 * @example
 * ```tsx
 * // Popup simple
 * const popupRef = useRef<PopupRef>(null);
 *
 * <Popup
 *   ref={popupRef}
 *   label="Abrir popup"
 *   position="bottom-right"
 * >
 *   <div>Contenido del popup</div>
 * </Popup>
 *
 * // Popup con control programático
 * <Popup
 *   ref={popupRef}
 *   label="Menú"
 *   position="top-left"
 * >
 *   <button onClick={() => popupRef.current?.close()}>Cerrar</button>
 * </Popup>
 *
 * // Abrir al pasar el ratón (`true` = 200 ms; o `openOnHover={400}` en ms)
 * <Popup label="Info" position="bottom-center" openOnHover>
 *   <p>Contenido</p>
 * </Popup>
 *
 * // Hover con panel anclado al puntero dentro del trigger (fluido, un frame por movimiento)
 * <Popup label="⋮" position="bottom-left" openOnHover hoverAnchorFollowPointer>
 *   <p>Menú</p>
 * </Popup>
 *
 * // Panel sin borde (fondo y sombra del DS se mantienen)
 * <Popup label="Opciones" showPanelBorder={false}>
 *   <p>Contenido</p>
 * </Popup>
 *
 * // Ancla fuera del Popup (`triggerRef` → sin botón interno; clic/hover en ese nodo)
 * const btnRef = useRef<HTMLButtonElement>(null);
 * <>
 *   <button ref={btnRef} type="button">Abrir</button>
 *   <Popup triggerRef={btnRef} position="bottom-center" popoverArrow={false}>
 *     <p>Contenido</p>
 *   </Popup>
 * </>
 *
 * // Popover estructurado (Figma); la flecha va por defecto (`popoverArrow="auto"`)
 * <Popup
 *   label="Abrir"
 *   position="bottom-center"
 *   popoverStructured
 *   popoverTitle="Título"
 *   popoverDescription="Descripción breve"
 *   popoverShowFooter
 *   popoverFooterSecondaryButton={{ id: 'c', label: 'Cancelar', color: 'secondary', theme: 'solid', size: 'xs', pill: 'sm' }}
 *   popoverFooterPrimaryButton={{ id: 'o', label: 'Aceptar', color: 'primary', theme: 'solid', size: 'xs', pill: 'sm' }}
 *   popoverFooterSlotLayout="spread"
 * >
 *   <p>Contenido</p>
 * </Popup>
 * ```
 *
 * @see {@link PopupProps} para la lista completa de props
 * @see {@link PopupRef} para métodos de control programático
 */
export const Popup = forwardRef<PopupRef, PopupProps>(
  (
    {
      children,
      label,
      position = 'bottom-right',
      offset = 6,
      closeOnClickOutside = true,
      closeOnEscape = true,
      popupId,
      className = '',
      showPanelBorder = true,
      onOpen,
      onClose,
      disabled = false,
      theme = 'solid',
      color = 'tertiary',
      size = 'xs',
      textColor,
      textAlign,
      rounded = false,
      leftSlot,
      borderRadius: _borderRadius = 'sm',
      rightSlot,
      icon,
      colorIcon,
      pill = 'xs',
      tooltip,
      tooltipPosition,
      tooltipAlignment,
      tooltipDelay,
      tooltipProps: tooltipPropsProp,
      absolute = false,
      allowOverflow = false,
      closeOnClick = true,
      buttonClassName,
      containerClassName,
      buttonTransition = true,
      buttonStyle,
      stopPropagationList = true,
      onClick,
      popoverArrow = 'auto',
      popoverStructured = false,
      popoverTitle,
      popoverDescription,
      popoverShowHeader = true,
      popoverShowCloseButton = true,
      popoverIconConfig,
      popoverBadge,
      popoverBadges,
      popoverHeaderButton,
      popoverHeaderButtons,
      popoverShowFooter = false,
      popoverFooterPrimaryButton,
      popoverFooterSecondaryButton,
      popoverFooterButtons,
      popoverFooterAuxButton,
      popoverFooterSlotLayout = 'inline',
      popoverBodyWithPadding = true,
      openOnHover,
      hoverAnchorFollowPointer,
      openOnHoverAllowPanelPointer = true,
      triggerRef: externalTriggerRefProp,
      fullWidth = false,
      // Resto de `ButtonProps` / `InnerButtonProps` del disparador (no deben aplicarse al `div` del panel)
      variant: _popupTriggerVariant,
      slotLayout,
      iconProps: userIconProps,
      leftSlotProps,
      rightSlotProps,
      badge: badgeConfig,
      link,
      target = '_self',
      as = 'button',
      clickable = true,
      square = false,
      borderable,
      whiteBackground = true,
      hoverText,
      active = false,
      stopPropagation = true,
      pointer = true,
      classNameLabel,
      classNameIcon = '',
      showShadow = false,
      strokeWidth = 2,
      classNameId = 'imj-ui-inner-button',
      onClickIcon,
      onClickLeftSlot,
      onClickRightSlot,
      iconDuotonePrimary,
      iconDuotoneSecondary,
      iconDuotoneOpacityPrimary,
      iconDuotoneOpacitySecondary,
      leftSlotDuotonePrimary,
      leftSlotDuotoneSecondary,
      leftSlotDuotoneOpacityPrimary,
      leftSlotDuotoneOpacitySecondary,
      rightSlotDuotonePrimary,
      rightSlotDuotoneSecondary,
      rightSlotDuotoneOpacityPrimary,
      rightSlotDuotoneOpacitySecondary,
      badgeColor,
      badgeLabel,
      badgeTheme = 'solid',
      badgeSize = 'sm',
      badgeStroke,
      badgeIcon,
      badgeIconDuotonePrimary,
      badgeIconDuotoneSecondary,
      badgeIconDuotoneOpacityPrimary,
      badgeIconDuotoneOpacitySecondary,
      badgeLoading,
      ...props
    },
    ref
  ) => {
    const usesExternalTrigger = Boolean(externalTriggerRefProp);

    const {
      isOpen,
      popupStyle,
      isPositioned,
      triggerRef,
      popupRef,
      contentRef,
      arrowPlacement,
      containerRef,
      popupUniqueId,
      togglePopup,
      closePopup,
      openPopup,
      openOnHoverTriggerProps,
      openOnHoverPopupProps,
    } = usePopup({
      position,
      offset,
      closeOnClickOutside,
      closeOnEscape,
      popupId,
      onOpen,
      onClose,
      disabled,
      absolute,
      popoverArrow,
      openOnHover,
      hoverAnchorFollowPointer,
      openOnHoverAllowPanelPointer,
      externalTriggerRef: externalTriggerRefProp,
    });

    // Exponer métodos a través de ref
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          if (!disabled) {
            openPopup();
          }
        },
        close: () => {
          closePopup();
        },
        isOpen: () => isOpen,
      }),
      [disabled, openPopup, closePopup, isOpen]
    );

    // Manejar el click del botón combinando togglePopup con onClick personalizado
    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Primero ejecutar el onClick personalizado si existe
      if (onClick) {
        onClick(e);
      }

      // Si el evento no fue cancelado, proceder con togglePopup
      if (!e.defaultPrevented && !disabled) {
        togglePopup();
      }
    };

    /**
     * Sin `textAlign` en el Popup: disparador inline sigue centrando la etiqueta (`center`);
     * con `slotLayout` spread, no forzar `center` en InnerButton para que aplique
     * `slotLayout.textAlign` (la prop `textAlign` del Popup sigue teniendo prioridad si se define).
     */
    const triggerTextAlign =
      textAlign ?? (isButtonSlotSpreadLayout(slotLayout) ? undefined : 'center');

    // Renderizar el popup con portal
    const renderPopup = () => {
      if (!isOpen) {
        return null;
      }

      const { style: panelStyleFromProps, ...restPanelProps } = props as {
        style?: React.CSSProperties;
      } & Record<string, unknown>;

      // Estilo inicial para que el popup sea invisible hasta que se posicione
      const initialStyle = {
        position: absolute ? ('absolute' as const) : ('fixed' as const),
        top: absolute ? 'auto' : '-9999px',
        left: absolute ? 'auto' : '-9999px',
        visibility: isPositioned ? ('visible' as const) : ('hidden' as const),
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.1s ease-out',
        ...popupStyle,
      };

      const panelClass = cn(
        allowOverflow ? 'ui-overflow-visible' : 'ui-overflow-hidden',
        'ui-rounded-surface-card ui-bg-fill',
        showPanelBorder ? 'ui-border ui-border-default ui-shadow-04' : 'ui-shadow-xl',
        className
      );

      const innerContent = popoverStructured ? (
        <PopupStructuredLayout
          isOpen={isOpen}
          onClose={closePopup}
          showHeader={popoverShowHeader}
          showFooter={popoverShowFooter}
          showCloseButton={popoverShowCloseButton}
          title={popoverTitle}
          description={popoverDescription}
          iconConfig={popoverIconConfig}
          badge={popoverBadge}
          badges={popoverBadges}
          headerButton={popoverHeaderButton}
          headerButtons={popoverHeaderButtons}
          footerPrimaryButton={popoverFooterPrimaryButton}
          footerSecondaryButton={popoverFooterSecondaryButton}
          footerButtons={popoverFooterButtons}
          footerAuxButton={popoverFooterAuxButton}
          footerSlotLayout={popoverFooterSlotLayout}
          bodyWithPadding={popoverBodyWithPadding}
        >
          {children}
        </PopupStructuredLayout>
      ) : (
        children
      );

      const popupContent = (
        <div
          ref={popupRef}
          id={popupUniqueId}
          data-popup-id={popupUniqueId}
          style={initialStyle}
          className="ui-relative"
          {...openOnHoverPopupProps}
        >
          {arrowPlacement ? (
            <PopupArrow placement={arrowPlacement} elevated={!showPanelBorder} />
          ) : null}
          <div
            ref={contentRef}
            className={panelClass}
            style={panelStyleFromProps}
            onClick={(e) => {
              if (stopPropagationList) {
                e.stopPropagation();
              }

              if (closeOnClick) {
                closePopup();
              }
            }}
            {...restPanelProps}
          >
            {innerContent}
          </div>
        </div>
      );

      // Si absolute es false, usar portal, si es true, renderizar directamente
      const result = absolute ? popupContent : createPortal(popupContent, document.body);

      return result;
    };

    // Detectar si icon es un objeto de configuración (ButtonIconConfig)
    // Un ButtonIconConfig tiene propiedades como 'color', 'size', 'show', etc.
    // Un objeto FontAwesome (IconDefinition) solo tiene 'icon', 'iconName', 'prefix', etc.
    const isIconConfigObject =
      icon &&
      typeof icon === 'object' &&
      !React.isValidElement(icon) &&
      'icon' in icon &&
      ('color' in icon ||
        'size' in icon ||
        'show' in icon ||
        'onClick' in icon ||
        'className' in icon ||
        'strokeWidth' in icon);

    // Si icon es un objeto de configuración, usarlo directamente
    const iconAsConfig = isIconConfigObject ? (icon as ButtonIconConfig) : undefined;

    // Convertir props legacy a configuraciones si no se proporcionan las nuevas
    // Solo convertir si icon NO es un objeto de configuración
    const computedIconProps = ((): ButtonIconConfig | undefined => {
      if (isIconConfigObject) return icon as ButtonIconConfig;
      if (!icon) return undefined;
      const cfg: ButtonIconConfig = {
        icon: icon as AnyIconDefinition,
        show: true,
      };
      if (colorIcon) cfg.color = colorIcon;
      if (classNameIcon) cfg.className = classNameIcon;
      if (onClickIcon) cfg.onClick = onClickIcon;
      if (iconDuotonePrimary) cfg.colorDuotonePrimary = iconDuotonePrimary;
      if (iconDuotoneSecondary) cfg.colorDuotoneSecondary = iconDuotoneSecondary;
      if (iconDuotoneOpacityPrimary !== undefined)
        cfg.opacityDuotonePrimary = iconDuotoneOpacityPrimary;
      if (iconDuotoneOpacitySecondary !== undefined) {
        cfg.opacityDuotoneSecondary = iconDuotoneOpacitySecondary;
      }
      return cfg;
    })();

    const mergedIconProps =
      userIconProps !== undefined && userIconProps !== null
        ? { ...(colorIcon ? { color: colorIcon } : {}), ...userIconProps }
        : computedIconProps;

    const resolvedLeftSlotProps =
      leftSlotProps !== null && leftSlotProps !== undefined
        ? { ...(colorIcon ? { colorIcon } : {}), ...leftSlotProps }
        : leftSlot && colorIcon
          ? { colorIcon }
          : undefined;

    const resolvedRightSlotProps =
      rightSlotProps !== null && rightSlotProps !== undefined
        ? { ...(colorIcon ? { colorIcon } : {}), ...rightSlotProps }
        : rightSlot && colorIcon
          ? { colorIcon }
          : undefined;

    // Detectar si tooltip es un objeto de configuración
    const isTooltipConfigObject =
      tooltip &&
      typeof tooltip === 'object' &&
      ('label' in tooltip ||
        'position' in tooltip ||
        'alignment' in tooltip ||
        'delay' in tooltip ||
        'absolute' in tooltip ||
        'className' in tooltip);

    // Si tooltip es un objeto de configuración, usarlo directamente
    const tooltipAsConfig = isTooltipConfigObject ? (tooltip as ButtonTooltipConfig) : undefined;

    // Convertir props legacy a configuraciones si no se proporcionan las nuevas
    // Solo convertir si tooltip NO es un objeto de configuración
    const legacyTooltipConfig = !isTooltipConfigObject
      ? convertLegacyTooltipToTooltipConfig({
          tooltip: tooltip as string | undefined,
          tooltipPosition,
          tooltipAlignment,
          tooltipDelay,
          tooltipAbsolute: undefined,
          classNameTooltip: undefined,
        })
      : undefined;

    // Prioridad: tooltipProps (nuevo) > tooltip como objeto > configuraciones legacy
    const tooltipProps = tooltipPropsProp || tooltipAsConfig || legacyTooltipConfig;

    return (
      <div
        ref={containerRef}
        className={cn('ui-imj ui-relative ui-h-max', fullWidth && 'ui-w-full')}
        data-popup-container-id={popupUniqueId}
      >
        {!usesExternalTrigger ? (
          <div
            ref={triggerRef}
            className={cn(fullWidth ? 'ui-w-full' : 'ui-w-fit', containerClassName)}
            {...openOnHoverTriggerProps}
          >
            <InnerButton
              color={color}
              className={buttonClassName}
              onClick={handleButtonClick}
              disabled={disabled}
              theme={theme}
              size={size}
              pill={pill}
              rounded={rounded}
              fullWidth={fullWidth}
              slotLayout={slotLayout}
              iconProps={mergedIconProps}
              leftSlot={leftSlot}
              rightSlot={rightSlot}
              leftSlotProps={resolvedLeftSlotProps}
              rightSlotProps={resolvedRightSlotProps}
              badge={badgeConfig}
              link={link}
              target={target}
              as={as}
              clickable={clickable}
              square={square}
              borderable={borderable}
              whiteBackground={whiteBackground}
              hoverText={hoverText}
              active={active}
              stopPropagation={stopPropagation}
              pointer={pointer}
              classNameLabel={classNameLabel}
              showShadow={showShadow}
              strokeWidth={strokeWidth}
              classNameId={classNameId}
              onClickLeftSlot={onClickLeftSlot}
              onClickRightSlot={onClickRightSlot}
              leftSlotDuotonePrimary={leftSlotDuotonePrimary}
              leftSlotDuotoneSecondary={leftSlotDuotoneSecondary}
              leftSlotDuotoneOpacityPrimary={leftSlotDuotoneOpacityPrimary}
              leftSlotDuotoneOpacitySecondary={leftSlotDuotoneOpacitySecondary}
              rightSlotDuotonePrimary={rightSlotDuotonePrimary}
              rightSlotDuotoneSecondary={rightSlotDuotoneSecondary}
              rightSlotDuotoneOpacityPrimary={rightSlotDuotoneOpacityPrimary}
              rightSlotDuotoneOpacitySecondary={rightSlotDuotoneOpacitySecondary}
              textAlign={triggerTextAlign}
              textColor={textColor}
              tooltipProps={tooltipProps}
              transition={buttonTransition}
              style={buttonStyle}
              badgeColor={badgeColor}
              badgeLabel={badgeLabel}
              badgeTheme={badgeTheme}
              badgeSize={badgeSize}
              badgeStroke={badgeStroke}
              badgeIcon={badgeIcon}
              badgeIconDuotonePrimary={badgeIconDuotonePrimary}
              badgeIconDuotoneSecondary={badgeIconDuotoneSecondary}
              badgeIconDuotoneOpacityPrimary={badgeIconDuotoneOpacityPrimary}
              badgeIconDuotoneOpacitySecondary={badgeIconDuotoneOpacitySecondary}
              badgeLoading={badgeLoading}
            >
              {label || null}
            </InnerButton>
          </div>
        ) : null}
        {renderPopup()}
      </div>
    );
  }
);

Popup.displayName = 'Popup';
